import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { allArticles, type Article as MockArticle } from "@/mocks/articles";

export type ArticleStatus = "pending" | "published" | "rejected";

export interface AdminArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  entrepreneur: string;
  country: string;
  countryFlag: string;
  category: string;
  sector: string;
  source: string;
  sourceUrl: string;
  originalUrl?: string;
  date: string;
  featured: boolean;
  views: number;
  readTime: number;
  status: ArticleStatus;
  createdAt: string;
}

function mapRow(row: Record<string, unknown>): AdminArticle {
  return {
    id: String(row.id ?? ""),
    title: String(row.title ?? ""),
    summary: String(row.summary ?? ""),
    image: String(row.image ?? ""),
    entrepreneur: String(row.entrepreneur ?? ""),
    country: String(row.country ?? ""),
    countryFlag: String(row.country_flag ?? "🌍"),
    category: String(row.category ?? ""),
    sector: String(row.sector ?? ""),
    source: String(row.source ?? ""),
    sourceUrl: String(row.source_url ?? ""),
    originalUrl: String(row.original_url ?? ""),
    date: String(row.date ?? ""),
    featured: Boolean(row.featured),
    views: Number(row.views ?? 0),
    readTime: Number(row.read_time ?? 5),
    status: (row.status as ArticleStatus) ?? "published",
    createdAt: String(row.created_at ?? ""),
  };
}

function mapMock(m: MockArticle): AdminArticle {
  return {
    id: m.id,
    title: m.title,
    summary: m.summary,
    image: m.image,
    entrepreneur: m.entrepreneur,
    country: m.country,
    countryFlag: m.countryFlag,
    category: m.category,
    sector: m.sector,
    source: m.source,
    sourceUrl: m.sourceUrl,
    date: m.date,
    featured: m.featured ?? false,
    views: m.views ?? 0,
    readTime: m.readTime ?? 5,
    status: "published",
    createdAt: m.date,
  };
}

const isSupabaseConfigured = (): boolean => {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  return Boolean(url && key && url !== "" && key !== "" && !url.includes("placeholder"));
};

export function useAdminArticles(statusFilter: ArticleStatus | "all" = "all") {
  const [articles, setArticles] = useState<AdminArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState({ pending: 0, published: 0, rejected: 0, total: 0 });

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!isSupabaseConfigured()) {
      // Mock fallback — simulate some pending articles
      const mockData = allArticles.map((a, i) => ({
        ...mapMock(a),
        status: (i % 5 === 0 ? "pending" : i % 7 === 0 ? "rejected" : "published") as ArticleStatus,
      }));
      const filtered = statusFilter === "all" ? mockData : mockData.filter((a) => a.status === statusFilter);
      setArticles(filtered);
      setCounts({
        pending: mockData.filter((a) => a.status === "pending").length,
        published: mockData.filter((a) => a.status === "published").length,
        rejected: mockData.filter((a) => a.status === "rejected").length,
        total: mockData.length,
      });
      setLoading(false);
      return;
    }

    try {
      // Fetch counts
      const { data: allData } = await supabase.from("articles").select("status");
      if (allData) {
        const c = { pending: 0, published: 0, rejected: 0, total: allData.length };
        allData.forEach((r) => {
          if (r.status === "pending") c.pending++;
          else if (r.status === "published") c.published++;
          else if (r.status === "rejected") c.rejected++;
        });
        setCounts(c);
      }

      // Fetch filtered articles
      let query = supabase.from("articles").select("*").order("created_at", { ascending: false });
      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }
      const { data, error: err } = await query;
      if (err) throw err;
      setArticles((data ?? []).map((r) => mapRow(r as Record<string, unknown>)));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erreur inconnue";
      setError(msg);
      const mockData = allArticles.map(mapMock);
      setArticles(statusFilter === "all" ? mockData : mockData.filter((a) => a.status === statusFilter));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const updateStatus = useCallback(async (id: string, newStatus: ArticleStatus): Promise<boolean> => {
    if (!isSupabaseConfigured()) {
      setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a)));
      return true;
    }
    try {
      const { error: err } = await supabase.from("articles").update({ status: newStatus }).eq("id", id);
      if (err) throw err;
      setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a)));
      setCounts((prev) => {
        const article = articles.find((a) => a.id === id);
        if (!article) return prev;
        const updated = { ...prev };
        updated[article.status]--;
        updated[newStatus]++;
        return updated;
      });
      return true;
    } catch {
      return false;
    }
  }, [articles]);

  const toggleFeatured = useCallback(async (id: string, featured: boolean): Promise<boolean> => {
    if (!isSupabaseConfigured()) {
      setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, featured } : a)));
      return true;
    }
    try {
      const { error: err } = await supabase.from("articles").update({ featured }).eq("id", id);
      if (err) throw err;
      setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, featured } : a)));
      return true;
    } catch {
      return false;
    }
  }, []);

  const triggerScrape = useCallback(async () => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseKey) return { success: false, error: "Supabase non configuré" };
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/rss-scraper`, {
        method: "POST",
        headers: { Authorization: `Bearer ${supabaseKey}`, "Content-Type": "application/json" },
      });
      const result = await res.json();
      await fetchArticles();
      return result;
    } catch (e) {
      return { success: false, error: String(e) };
    }
  }, [fetchArticles]);

  return { articles, loading, error, counts, updateStatus, toggleFeatured, triggerScrape, refetch: fetchArticles };
}
